import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import HeroEditorial from "../components/HeroEditorial";
import HeroCinematic from "../components/HeroCinematic";
import HeroBoutique from "../components/HeroBoutique";
import EditorialMarquee from "../components/EditorialMarquee";
import CampaignBanner from "../components/CampaignBanner";
import Manifesto from "../components/Manifesto";
import Collections from "../components/Collections";
import ProductCard from "../components/ProductCard";
import ProductCarousel3D from "../components/ProductCarousel3D";
import PerfumeAtelier from "../components/PerfumeAtelier";
import { TrustBar } from "../components/BrandSections";
import PriveLaunch from "../components/PriveLaunch";
import PriveSpotlight from "../components/PriveSpotlight";
import RecentlyViewed from "../components/RecentlyViewed";
import Reveal from "../components/Reveal";

function SectionHeader({ num, eyebrow, title, accent, link, linkLabel, testId }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-6 mb-10 md:mb-14">
      <Reveal>
        <p className="text-[10px] md:text-xs tracking-[0.5em] uppercase gold-text mb-5"><span className="font-serif italic text-2xl mr-4 align-middle">{num}</span>{eyebrow}</p>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">
          {title} {accent && <span className="italic gold-text">{accent}</span>}
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <Link to={link} data-testid={testId} className="group flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-ivory/60 hover:text-gold-light transition-colors duration-300">
          {linkLabel}
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  );
}

export default function Home() {
  const [all, setAll] = useState([]);
  const [heroStyle, setHeroStyle] = useState(() => new URLSearchParams(window.location.search).get("hero") || localStorage.getItem("atw_hero") || "boutique");

  useEffect(() => {
    api.get("/products").then((r) => setAll(r.data)).catch(() => {});
    if (!new URLSearchParams(window.location.search).get("hero")) {
      api.get("/settings").then((r) => { const h = r.data.hero_style || "boutique"; setHeroStyle(h); localStorage.setItem("atw_hero", h); }).catch(() => {});
    }
  }, []);

  const perfumes = all.filter((p) => p.category === "Perfumes" && !p.prive_only);
  const featured = all.filter((p) => p.featured);
  const arrivals = all.filter((p) => !p.featured).slice(0, 8);

  return (
    <main data-testid="home-page" className="bg-obsidian text-ivory">
      {heroStyle === "cinematic" ? <HeroCinematic /> : heroStyle === "editorial" ? <HeroEditorial /> : <HeroBoutique products={all} />}
      <PriveSpotlight />
      <TrustBar />
      <CampaignBanner />

      <section id="featured" data-testid="featured-section" className="relative py-20 md:py-28 px-6 lg:px-12 overflow-hidden bg-obsidian">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_70%,rgba(212,175,55,0.16),transparent_55%)]" />
        <div className="relative max-w-[1400px] mx-auto min-h-[640px]">
          <SectionHeader num="01" eyebrow="The Icons" title="Signature" accent="Pieces" link="/shop" linkLabel="Shop all" testId="featured-view-all-link" />
          <Reveal depth={false} y={60}>
            <ProductCarousel3D products={featured} />
          </Reveal>
        </div>
      </section>

      <PerfumeAtelier perfumes={perfumes} />
      <Collections />
      <Manifesto />
      <PriveLaunch />

      <section data-testid="arrivals-section" className="relative py-20 md:py-28 px-6 lg:px-12 bg-coal">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_10%,rgba(212,175,55,0.1),transparent_50%)]" />
        <div className="relative max-w-[1400px] mx-auto">
          <SectionHeader num="02" eyebrow="Fresh from the atelier" title="New" accent="Arrivals" link="/shop" linkLabel="View the collection" testId="arrivals-view-all-link" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 md:gap-x-8 gap-y-14">
            {arrivals.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} dark />
            ))}
          </div>
        </div>
      </section>

      <RecentlyViewed />

      <section data-testid="cta-section" className="relative py-28 md:py-40 px-6 overflow-hidden bg-obsidian text-ivory">
        <img src="/brand/campaign-hero.jpg" alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/50 to-obsidian" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,rgba(212,175,55,0.22),transparent_55%)]" />
        <div className="relative max-w-[1400px] mx-auto text-center">
          <Reveal>
            <img src="/brand/logo-seal.png" alt="ATW seal" className="w-24 h-24 mx-auto mb-8 drop-shadow-[0_0_40px_rgba(212,175,55,0.7)] animate-float-slow" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] uppercase">
              Wear the <span className="italic gold-text">Name</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/shop" data-testid="cta-shop-button" className="gold-button inline-block mt-12 px-10 py-5 text-[11px] tracking-[0.3em] uppercase font-medium">
              <span className="relative z-10">Enter the Store</span>
            </Link>
          </Reveal>
        </div>
      </section>
      <EditorialMarquee />
    </main>
  );
}
